import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const signed = (u) => (!u ? null : /^https?:/.test(u) ? u : `${BACKEND}${u}`);

/**
 * Full-screen image viewer. Given a list of media ids and a starting index,
 * shows the full-size image with keyboard/click paging and Esc-to-close.
 *
 * Props:
 *   ids      – array of media ids to page through
 *   index    – current index into `ids`
 *   onIndex  – (nextIndex) => void   (page to another image)
 *   onClose  – () => void
 */
export function Lightbox({ ids, index, onIndex, onClose }) {
  const count = ids.length;
  const id = ids[index];
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch the full-size (not thumbnail) signed URL for the current image.
  useEffect(() => {
    let cancel = false;
    setUrl(null);
    setLoading(true);
    if (!id) return;
    api.get(`/media/${id}/file`)
      .then((r) => { if (!cancel) { setUrl(signed(r.data?.url)); setLoading(false); } })
      .catch(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [id]);

  // Keyboard: Esc closes, arrows page.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (count > 1 && e.key === "ArrowRight") onIndex((index + 1) % count);
      else if (count > 1 && e.key === "ArrowLeft") onIndex((index - 1 + count) % count);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, count, onClose, onIndex]);

  const go = (delta) => (e) => { e.stopPropagation(); onIndex((index + delta + count) % count); };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center select-none"
      onClick={onClose}
      data-testid="lightbox"
    >
      <button
        type="button"
        className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Close"
        data-testid="lightbox-close"
      >
        <X className="w-7 h-7" />
      </button>

      {count > 1 && (
        <button
          type="button"
          className="absolute left-2 sm:left-4 text-white/70 hover:text-white transition-colors"
          onClick={go(-1)}
          aria-label="Previous image"
        >
          <ChevronLeft className="w-9 h-9" />
        </button>
      )}

      <div className="max-w-[92vw] max-h-[92vh] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
        {loading ? (
          <div className="text-white/70 text-sm">Loading…</div>
        ) : url ? (
          <img
            src={url}
            alt=""
            className="max-w-[92vw] max-h-[92vh] object-contain rounded"
            data-testid="lightbox-image"
          />
        ) : (
          <div className="text-white/70 text-sm">Couldn't load this image.</div>
        )}
      </div>

      {count > 1 && (
        <>
          <button
            type="button"
            className="absolute right-2 sm:right-4 text-white/70 hover:text-white transition-colors"
            onClick={go(1)}
            aria-label="Next image"
          >
            <ChevronRight className="w-9 h-9" />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-xs font-mono" data-testid="lightbox-counter">
            {index + 1} / {count}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * A row of clickable image thumbnails that open the Lightbox. `ids` is the full
 * list; only the first `max` thumbnails are shown (with a “+N” overflow), but
 * the Lightbox pages through ALL of them.
 */
export function ImageThumbs({ ids, size = 96, max = 4, renderThumb }) {
  const [open, setOpen] = useState(null); // current index, or null when closed
  if (!ids || ids.length === 0) return <span>—</span>;
  return (
    <div className="flex items-center gap-1.5">
      {ids.slice(0, max).map((id, i) => (
        <button
          type="button"
          key={`${id}-${i}`}
          onClick={(e) => { e.stopPropagation(); setOpen(i); }}
          className="rounded-md overflow-hidden cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
          title="Click to view full size"
          data-testid={`image-thumb-open-${i}`}
        >
          {renderThumb(id, size)}
        </button>
      ))}
      {ids.length > max && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setOpen(max); }}
          className="text-xs text-muted-foreground hover:text-foreground"
          title="View all"
        >
          +{ids.length - max}
        </button>
      )}
      {open !== null && (
        <Lightbox ids={ids} index={open} onIndex={setOpen} onClose={() => setOpen(null)} />
      )}
    </div>
  );
}
